import { type FC, type MouseEventHandler, type MouseEvent } from 'react';
import classnames from 'classnames';
import { useIntl, defineMessages } from 'dibs-react-intl';
import {
    trackEvent,
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
} from 'dibs-tracking';
const { EVENT_NAVIGATION } = eventNameConstants;
const { INTERACTION_TYPE_GLOBAL_NAV } = interactionTypeConstants;
const { STEP_INTERACTION_GLOBAL_NAV_CART_DROPDOWN_CLICK } = stepInteractionConstants;
import { Link } from 'dibs-elements/exports/Link.tsx';
import ShoppingBagIcon from 'dibs-icons/exports/legacy/ShoppingBag.ts';

import dibsCss from 'dibs-css';
import styles from './CartNav-style.scss';

const MAX_CART_COUNT_DISPLAY = 9;

function handleOnClick(event: MouseEvent): void {
    const label = 'cart|cart header link';
    trackEvent(
        {
            category: 'navigation',
            action: 'header navigation click',
            label,
            eventName: EVENT_NAVIGATION,
            interaction_type: INTERACTION_TYPE_GLOBAL_NAV,
            step_interaction_name: STEP_INTERACTION_GLOBAL_NAV_CART_DROPDOWN_CLICK,
            trigger: label,
        },
        event
    );
}

type CartNavDropdownTriggerProps = {
    count: number;
    handleMouseEnter?: MouseEventHandler;
};

const CartNavCount: FC<{ count: number }> = ({ count }) => {
    const hasMaxCartItems = count === MAX_CART_COUNT_DISPLAY;
    return (
        <div className={classnames(dibsCss.absolute, dibsCss.wFull)}>
            <div
                className={classnames(
                    styles.count,
                    dibsCss.sassyFontBodySizeXxSmall,
                    dibsCss.relative,
                    dibsCss.bottom3px,
                    dibsCss.bgPitchblack,
                    dibsCss.textWhite,
                    dibsCss.w16px,
                    dibsCss.h16px,
                    dibsCss.textCenter,
                    dibsCss.roundedFull,
                    hasMaxCartItems && dibsCss.pl3px
                )}
            >
                {count}
                {hasMaxCartItems ? <sup>+</sup> : null}
            </div>
        </div>
    );
};

const messages = defineMessages({
    label: { id: 'dbl.Header.CartNavDropdownTrigger.label', defaultMessage: 'Cart' },
});
const CartNavDropdownTrigger: FC<CartNavDropdownTriggerProps> = ({
    count = 0,
    handleMouseEnter = () => {},
}) => {
    count = Math.min(count, MAX_CART_COUNT_DISPLAY);
    const intl = useIntl();

    return (
        <div
            data-tn="cart-nav-dropdown-trigger"
            className={classnames(
                dibsCss.wLarge,
                dibsCss.flex,
                dibsCss.itemsCenter,
                dibsCss.justifyCenter
            )}
            onMouseEnter={handleMouseEnter}
        >
            <Link
                className={classnames(
                    styles.icon,
                    dibsCss.cursorPointer,
                    dibsCss.relative,
                    dibsCss.flex,
                    dibsCss.wSmall,
                    dibsCss.hSmall
                )}
                href="/my/cart/"
                ariaLabel={intl.formatMessage(messages.label)}
                onClick={handleOnClick}
            >
                {count ? <CartNavCount count={count} /> : null}
                <ShoppingBagIcon />
            </Link>
        </div>
    );
};

export default CartNavDropdownTrigger;
